import React, { useEffect } from 'react';
import { Button, Select, Stack, Text } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { User } from '../../utils/session';
import { Account } from '../AccountCard';
import { SyncRuleFormValues } from '../sync_rule/interface';
import { buildDestinationCalendarOptions, countEligibleDestinations } from '../sync_rule/calendarOptions';
import { DestinationMode } from './state';
import { OnboardingConnectAccount } from './OnboardingConnectAccount';

export const OnboardingDestinationSelect: React.FC<{
    accounts: Account[];
    user: User;
    form: UseFormReturnType<SyncRuleFormValues>;
    destinationMode: DestinationMode;
    onDestinationModeChange: (mode: DestinationMode) => void;
    updateAccounts: () => void;
}> = ({
    accounts,
    user,
    form,
    destinationMode,
    onDestinationModeChange,
    updateAccounts,
}) => {
    const { t } = useTranslation(['app']);

    const eligibleCount = countEligibleDestinations(accounts, form.values.source);
    const destinationCalendars = buildDestinationCalendarOptions(accounts, t, form.values.source);
    const connectingAnother = destinationMode === 'connect_another';
    const showDestinationSelect = !connectingAnother || accounts.length >= 2;

    useEffect(() => {
        if (eligibleCount === 0 && destinationMode !== 'connect_another') {
            onDestinationModeChange('connect_another');
        }
    }, [eligibleCount, destinationMode, onDestinationModeChange]);

    return (
        <Stack gap="lg">
            <Stack gap="xs">
                <Text size="xl" fw={600}>{t('dashboard.onboarding.step3.title')}</Text>
                <Text c="dimmed">{t('dashboard.onboarding.step3.description')}</Text>
            </Stack>

            {eligibleCount > 0 && (
                <Stack gap="sm">
                    <Button
                        variant={destinationMode === 'pick_existing' ? 'filled' : 'outline'}
                        onClick={() => {
                            onDestinationModeChange('pick_existing');
                            form.setFieldValue('destination', '');
                        }}
                    >
                        {t('dashboard.onboarding.step3.pick-existing')}
                    </Button>
                    <Button
                        variant={connectingAnother ? 'filled' : 'outline'}
                        onClick={() => {
                            onDestinationModeChange('connect_another');
                            form.setFieldValue('destination', '');
                        }}
                    >
                        {t('dashboard.onboarding.step3.connect-another')}
                    </Button>
                </Stack>
            )}

            {eligibleCount === 0 && (
                <Text size="sm" c="dimmed">{t('dashboard.onboarding.step3.no-destinations-hint')}</Text>
            )}

            {connectingAnother && (
                <OnboardingConnectAccount
                    user={user}
                    accounts={accounts}
                    updateAccounts={updateAccounts}
                    hideHeader
                />
            )}

            {showDestinationSelect && eligibleCount > 0 && (
                <Select
                    placeholder={t('dashboard.sync.draft.destination')}
                    data={destinationCalendars}
                    {...form.getInputProps('destination')}
                    searchable
                    required
                    size="md"
                    comboboxProps={{ styles: { search: { fontSize: '16px' } } }}
                />
            )}
        </Stack>
    );
};
